import type { VisibilityState } from '@/components/UI/dataTableConfig';
import { durationValues, HIDEABLE_COLUMN_IDS, type CompareDuration, type HideableColumnId, type StagePresenceFilter } from './constants';
import type { ComparePageState, CompareRow, CompareSortBy, OptionsFilter } from './types';

export function parseHiddenColumns(value: string | null): HideableColumnId[] {
    if (!value) return [];
    const set = new Set<HideableColumnId>();

    for (const token of value.split(',')) {
        const trimmed = token.trim();

        if ((HIDEABLE_COLUMN_IDS as readonly string[]).includes(trimmed)) {
            set.add(trimmed as HideableColumnId);
        }
    }

    return [...set];
}

export function hiddenColumnsToVisibility(hidden: HideableColumnId[]): VisibilityState {
    const visibility: VisibilityState = {};

    for (const id of hidden) {
        visibility[id] = false;
    }

    return visibility;
}

export function parseOptionalPositiveInt(value: string | null): number | null {
    if (!value) return null;

    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    const floored = Math.floor(parsed);

    return floored > 0 ? floored : null;
}

export function parseOptionalNonZeroInt(value: string | null): number | null {
    if (!value) return null;

    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    const floored = Math.floor(parsed);

    return floored !== 0 ? floored : null;
}

export function parseCompareDuration(value: string | null): CompareDuration | null {
    if (!value) {
        return null;
    }

    return durationValues.includes(value as CompareDuration) ? (value as CompareDuration) : null;
}

export function parseStagePresenceFilter(value: string | null): StagePresenceFilter {
    return value === 'yes' || value === 'no' ? value : 'all';
}

export function stagePresenceToBoolean(value: StagePresenceFilter): boolean | null {
    if (value === 'yes') return true;
    if (value === 'no') return false;
    return null;
}

export function formatPercent(value: number | null | undefined, digits: number = 2): string {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(digits)}%`;
}

export function valueClass(value: number | null | undefined, invert = false): string {
    if (value === null || value === undefined) return 'text-zinc-500';
    if (value === 0) return 'text-zinc-300';

    const isPositive = invert ? value < 0 : value > 0;
    return isPositive ? 'text-green-400' : 'text-red-400';
}

export { heatmapStyle } from '@/utils/pnlHeatmap';

export function keyForRow(row: Pick<CompareRow, 'experimentId' | 'duration' | 'horizonSeconds'>): string {
    return `${row.experimentId}:${row.duration}:${row.horizonSeconds}`;
}

export function formatStrategyLabel(strategyId: number, strategyName: string | null | undefined): string {
    return `${strategyName ?? `Strategy ${strategyId}`} (#${strategyId})`;
}

const allowedSortKeys: CompareSortBy[] = [
    'experimentName',
    'strategyName',
    'completedAt',
    'duration',
    'horizonSeconds',
    'totalEntries',
    'totalEntities',
    'avgProfitPercent',
    'medianProfitPercent',
    'avgAlphaPercent',
    'winRate',
    'winRateVsSpy',
    'profitFactor',
    'coefficientOfVariation',
    'totalProfitPercent',
    'sharpeLike',
    'maxDrawdownPercent',
    'optionAvgProfitPercent',
    'optionMedianProfitPercent',
    'optionWinRate'
];

export function parseComparePageState(searchParams: URLSearchParams): ComparePageState {
    const selectedStrategyId = parseOptionalPositiveInt(searchParams.get('strategyId'));
    const defaultSortBy: CompareSortBy = selectedStrategyId === null ? 'avgAlphaPercent' : 'avgProfitPercent';
    const rawOptions = searchParams.get('options');
    const optionsFilter: OptionsFilter = rawOptions === 'with' ? 'with' : rawOptions === 'without' ? 'without' : 'all';
    const rawSortBy = searchParams.get('sortBy');

    return {
        selectedStrategyId,
        compareMode: searchParams.get('mode') === 'representative' ? 'representative' : 'fixed',
        selectedDuration: parseCompareDuration(searchParams.get('duration')),
        selectedHorizonSeconds: parseOptionalNonZeroInt(searchParams.get('horizonSeconds')),
        search: searchParams.get('search') ?? '',
        optionsFilter,
        hasScorecardsFilter: parseStagePresenceFilter(searchParams.get('scorecards')),
        strategyHasBacktestsFilter: parseStagePresenceFilter(searchParams.get('backtests')),
        strategyHasPortfolioBacktestsFilter: parseStagePresenceFilter(searchParams.get('portfolioBacktests')),
        strategyHasPortfolioFilter: parseStagePresenceFilter(searchParams.get('portfolio')),
        strategyIsCurrentChampionFilter: parseStagePresenceFilter(searchParams.get('champion')),
        minTotalEntries: parseOptionalPositiveInt(searchParams.get('minTotalEntries')),
        minTotalEntities: parseOptionalPositiveInt(searchParams.get('minTotalEntities')),
        currentPage: parseOptionalPositiveInt(searchParams.get('page')) ?? 1,
        sortBy: allowedSortKeys.includes(rawSortBy as CompareSortBy) ? (rawSortBy as CompareSortBy) : defaultSortBy,
        sortDirection: searchParams.get('sortDirection') === 'asc' ? 'asc' : 'desc',
        hiddenColumns: parseHiddenColumns(searchParams.get('hiddenCols')),
        filtersCollapsed: searchParams.get('filters') === 'collapsed'
    };
}

export function buildComparePageSearchParams(pageState: ComparePageState): URLSearchParams {
    const defaultSortBy: CompareSortBy = pageState.selectedStrategyId === null ? 'avgAlphaPercent' : 'avgProfitPercent';
    const searchParams = new URLSearchParams();

    if (pageState.selectedStrategyId !== null) {
        searchParams.set('strategyId', String(pageState.selectedStrategyId));
    }

    if (pageState.compareMode !== 'fixed') {
        searchParams.set('mode', pageState.compareMode);
    }

    if (pageState.selectedDuration !== null) {
        searchParams.set('duration', pageState.selectedDuration);
    }

    if (pageState.selectedHorizonSeconds !== null) {
        searchParams.set('horizonSeconds', String(pageState.selectedHorizonSeconds));
    }

    if (pageState.search.trim()) {
        searchParams.set('search', pageState.search);
    }

    if (pageState.optionsFilter !== 'all') {
        searchParams.set('options', pageState.optionsFilter);
    }

    if (pageState.hasScorecardsFilter !== 'all') {
        searchParams.set('scorecards', pageState.hasScorecardsFilter);
    }

    if (pageState.strategyHasBacktestsFilter !== 'all') {
        searchParams.set('backtests', pageState.strategyHasBacktestsFilter);
    }

    if (pageState.strategyHasPortfolioBacktestsFilter !== 'all') {
        searchParams.set('portfolioBacktests', pageState.strategyHasPortfolioBacktestsFilter);
    }

    if (pageState.strategyHasPortfolioFilter !== 'all') {
        searchParams.set('portfolio', pageState.strategyHasPortfolioFilter);
    }

    if (pageState.strategyIsCurrentChampionFilter !== 'all') {
        searchParams.set('champion', pageState.strategyIsCurrentChampionFilter);
    }

    if (pageState.minTotalEntries !== null) {
        searchParams.set('minTotalEntries', String(pageState.minTotalEntries));
    }

    if (pageState.minTotalEntities !== null) {
        searchParams.set('minTotalEntities', String(pageState.minTotalEntities));
    }

    if (pageState.currentPage > 1) {
        searchParams.set('page', String(pageState.currentPage));
    }

    if (pageState.sortBy !== defaultSortBy) {
        searchParams.set('sortBy', pageState.sortBy);
    }

    if (pageState.sortDirection !== 'desc') {
        searchParams.set('sortDirection', pageState.sortDirection);
    }

    if (pageState.hiddenColumns.length > 0) {
        searchParams.set('hiddenCols', pageState.hiddenColumns.join(','));
    }

    if (pageState.filtersCollapsed) {
        searchParams.set('filters', 'collapsed');
    }

    return searchParams;
}
